import { useCallback, useEffect, useMemo, useReducer } from 'react'
import {
  getStopsFilterItems,
  getAirlineFilterItems,
  getDurationFilterItems,
  getPricesFilterItems,
  getDepartureTimesFilterItems,
  getArrivalTimesFilterItems,
  getBagsFilterItems,
  isJourneyVisible,
} from 'lib/flights/flightFilterUtils'
import { getNextFlightFilters } from 'lib/flights/flightUtils'
import standaloneFlightFiltersReducer, {
  StandaloneFlightsFiltersAction,
  initialState as initialFiltersState,
} from 'contexts/Flights/StandaloneFlightsFilters/standaloneFlightsFiltersReducer'
import { FlightFilterType } from 'constants/flight'
import { FlightsFiltersPriceType } from 'lib/flights/flightFilterTypes'
import type { FlightsFiltersValues } from 'lib/flights/flightFilterTypes'

function isJourneyVisibleForTripFilters(journey: App.JourneyV2, filters: FlightsFiltersValues): boolean {
  if (!isJourneyVisible(journey, filters)) {
    return false
  }

  const price = filters.priceFilterType === FlightsFiltersPriceType.ADULT ? journey.price.adult.totalFare : journey.price.all.totalFare

  if (filters.minPrices && (price === undefined || price < parseInt(filters.minPrices))) {
    return false
  }

  if (filters.maxPrices && (price === undefined || price > parseInt(filters.maxPrices))) {
    return false
  }

  return true
}

/**
 * Local-state flight filter hook for the trip planner surfaces (drawer + chat).
 *
 * Reuses the standalone search filter engine — the same reducer, the same
 * `getNextFlightFilters` transition helper, the same `get*FilterItems` option
 * builders, and the same pure `isJourneyVisible` matcher — but holds the filter
 * state in local `useReducer` state instead of the global standalone context, so
 * trip surfaces don't share or clobber the main search's filters.
 *
 * Scope is the "Core 6" trip filters: stops, bags, airlines, duration,
 * departure/arrival time, and price.
 */
export function useTripFlightFilters(
  journeys: Array<App.JourneyV2>,
  options?: { resetKey?: string },
) {
  const resetKey = options?.resetKey

  const [filters, dispatch] = useReducer(standaloneFlightFiltersReducer, initialFiltersState)

  // Reset all filters whenever the underlying result set changes (new search or
  // a different segment), matching the "reset on new search" behaviour.
  useEffect(() => {
    dispatch({ type: StandaloneFlightsFiltersAction.RESET_FILTERS })
  }, [resetKey])

  const stopItems = useMemo(() => getStopsFilterItems(journeys), [journeys])
  const airlineItems = useMemo(() => getAirlineFilterItems(journeys), [journeys])
  const durationItems = useMemo(() => getDurationFilterItems(journeys), [journeys])
  const priceItems = useMemo(() => getPricesFilterItems(filters.priceFilterType, journeys), [filters.priceFilterType, journeys])
  const departureTimeItems = useMemo(() => getDepartureTimesFilterItems(journeys), [journeys])
  const arrivalTimeItems = useMemo(() => getArrivalTimesFilterItems(journeys), [journeys])
  const bagItems = useMemo(() => getBagsFilterItems(journeys), [journeys])

  // Empty selection means "all", which the matcher treats as no constraint.
  const selectedStops = useMemo(() => new Set(filters.stops.length ? filters.stops : ['all']), [filters.stops])
  const selectedAirlines = useMemo(() => new Set(filters.airlines.length ? filters.airlines : ['all']), [filters.airlines])
  const selectedBags = useMemo(() => new Set(filters.bags), [filters.bags])
  const selectedDurations = useMemo(() => new Set(filters.durations ? [filters.durations] : []), [filters.durations])
  const selectedMinPrices = useMemo(() => new Set(filters.minPrices ? [filters.minPrices] : []), [filters.minPrices])
  const selectedMaxPrices = useMemo(() => new Set(filters.maxPrices ? [filters.maxPrices] : []), [filters.maxPrices])
  const selectedMinDepartureTime = useMemo(() => new Set(filters.minDepartureTime ? [filters.minDepartureTime] : []), [filters.minDepartureTime])
  const selectedMaxDepartureTime = useMemo(() => new Set(filters.maxDepartureTime ? [filters.maxDepartureTime] : []), [filters.maxDepartureTime])
  const selectedMinArrivalTime = useMemo(() => new Set(filters.minArrivalTime ? [filters.minArrivalTime] : []), [filters.minArrivalTime])
  const selectedMaxArrivalTime = useMemo(() => new Set(filters.maxArrivalTime ? [filters.maxArrivalTime] : []), [filters.maxArrivalTime])

  const onFilterChange = useCallback((name: keyof FlightsFiltersValues, value: string) => {
    const nextFilter = getNextFlightFilters(name, value, filters)
    dispatch({ type: StandaloneFlightsFiltersAction.SET_FLIGHTS_FILTER, filters: nextFilter })
  }, [filters])

  const onPriceTypeChange = useCallback((priceFilterType: FlightsFiltersPriceType) => {
    dispatch({ type: StandaloneFlightsFiltersAction.SET_FLIGHTS_FILTER, filters: { priceFilterType } })
  }, [])

  const resetAllFilters = useCallback(() => {
    dispatch({ type: StandaloneFlightsFiltersAction.RESET_FILTERS })
  }, [])

  const visibleJourneys = useMemo(
    () => journeys.filter(journey => isJourneyVisibleForTripFilters(journey, filters)),
    [journeys, filters],
  )

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.stops.length) count++
    if (filters.bags.length) count++
    if (filters.airlines.length) count++
    if (filters.durations) count++
    if (filters.minPrices || filters.maxPrices) count++
    if (filters.minDepartureTime || filters.maxDepartureTime || filters.minArrivalTime || filters.maxArrivalTime) count++
    return count
  }, [filters])

  return {
    filters,
    onFilterChange,
    onPriceTypeChange,
    resetAllFilters,
    visibleJourneys,
    activeFilterCount,
    priceType: filters.priceFilterType,
    filterItems: {
      [FlightFilterType.STOPS]: stopItems,
      [FlightFilterType.AIRLINES]: airlineItems,
      [FlightFilterType.DURATIONS]: durationItems,
      [FlightFilterType.PRICES]: priceItems,
      [FlightFilterType.DEPARTURE_TIMES]: departureTimeItems,
      [FlightFilterType.ARRIVAL_TIMES]: arrivalTimeItems,
      [FlightFilterType.BAGS]: bagItems,
    },
    activeFilters: {
      [FlightFilterType.STOPS]: selectedStops,
      [FlightFilterType.AIRLINES]: selectedAirlines,
      [FlightFilterType.BAGS]: selectedBags,
      [FlightFilterType.DURATIONS]: selectedDurations,
      [FlightFilterType.MIN_PRICES]: selectedMinPrices,
      [FlightFilterType.MAX_PRICES]: selectedMaxPrices,
      [FlightFilterType.MIN_DEPARTURE_TIME]: selectedMinDepartureTime,
      [FlightFilterType.MAX_DEPARTURE_TIME]: selectedMaxDepartureTime,
      [FlightFilterType.MIN_ARRIVAL_TIME]: selectedMinArrivalTime,
      [FlightFilterType.MAX_ARRIVAL_TIME]: selectedMaxArrivalTime,
    },
  }
}

export type UseTripFlightFiltersResult = ReturnType<typeof useTripFlightFilters>
